import type { Observable } from 'rxjs'
import type { ListBundlesFn } from '@/features/admin'
import type {
  GetCurrentUserFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetWorkspaceRolesFn,
  GetWorkspaceSelectionFn,
  GetWorkspaceSkillsFn,
  ListRolesFn,
  ListSkillsFn,
  PrimeMemberFn,
} from '@/features/iam'
import type {
  FetchInventoryItemExportsFn,
  ImportInventoryItemsFn,
  ListInventoryItemsFn,
} from '@/features/inventory'
import type { QuestionListViewModelFactory } from '@/features/jobs'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { NavigateFn } from '@/RootStore'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import { canViewWorkspaceLegends } from './legend-access'

/**
 * Options for {@link LegendsFeature}.
 */
export interface LegendsFeatureOptions {
  token$: Observable<string>
  navigate: NavigateFn
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
  getCurrentUser: GetCurrentUserFn
  getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn
  getWorkspaceSelection: GetWorkspaceSelectionFn
  primeMember: PrimeMemberFn
  listRoles: ListRolesFn
  getWorkspaceRoles: GetWorkspaceRolesFn
  listSkills: ListSkillsFn
  getWorkspaceSkills: GetWorkspaceSkillsFn
  listInventoryItems: ListInventoryItemsFn
  fetchInventoryItemExports: FetchInventoryItemExportsFn
  importInventoryItems: ImportInventoryItemsFn
  listBundles: ListBundlesFn
  createQuestionListViewModel: PromiseLike<QuestionListViewModelFactory>
}

/**
 * Reactively determines whether the user may see the legends of any of their
 * workspaces, which is when the navigation offers them.
 */
export type CanViewLegendsFn = () => boolean

/**
 * The legend builder: the workspaces' legends and their editor.
 */
export type LegendsFeature = ReturnType<typeof LegendsFeature>

export function LegendsFeature(options: LegendsFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (m) => m.InternalLegendsFeature(options),
  )

  const canViewLegends: CanViewLegendsFn = () => {
    const user = options.getCurrentUser()
    return options
      .getCurrentUserWorkspaceMemberships()
      .some((membership) => canViewWorkspaceLegends(membership, user))
  }

  return {
    // Internal
    internal,

    // Public
    canViewLegends,
  }
}
